import React, { useState, useEffect, useMemo } from "react";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiFilter,
  FiCheck,
  FiAlertTriangle,
} from "react-icons/fi";
import "./DQTPage.css";

// 예시 데이터 - 실제 애플리케이션에서는 API 호출로 대체
const generateDummyData = () => {
  const data = [];
  const variables = [
    "age",
    "weight",
    "height",
    "bmi",
    "heart_rate",
    "systolic",
    "diastolic",
    "temperature",
    "saturation",
  ];
  const statuses = ["정상", "경고", "오류"];

  for (let i = 1; i <= 50; i++) {
    const randomVar = variables[Math.floor(Math.random() * variables.length)];
    const record = {
      id: i,
      subjectId: `OHCA-${String(i).padStart(3, "0")}`,
      variableName: randomVar,
      value:
        randomVar === "age"
          ? Math.floor(Math.random() * 70 + 10)
          : randomVar === "weight"
          ? Math.floor(Math.random() * 50 + 40)
          : randomVar === "height"
          ? Math.floor(Math.random() * 50 + 150)
          : randomVar === "bmi"
          ? (Math.random() * 15 + 18).toFixed(1)
          : randomVar === "heart_rate"
          ? Math.floor(Math.random() * 50 + 60)
          : randomVar === "systolic"
          ? Math.floor(Math.random() * 60 + 100)
          : randomVar === "diastolic"
          ? Math.floor(Math.random() * 30 + 60)
          : randomVar === "temperature"
          ? (Math.random() * 2 + 36).toFixed(1)
          : Math.floor(Math.random() * 10 + 90),
      status: statuses[Math.floor(Math.random() * statuses.length)],
      createdDate: new Date(
        2023,
        Math.floor(Math.random() * 12),
        Math.floor(Math.random() * 28) + 1
      )
        .toISOString()
        .split("T")[0],
      updatedDate: new Date(
        2023,
        Math.floor(Math.random() * 12),
        Math.floor(Math.random() * 28) + 1
      )
        .toISOString()
        .split("T")[0],
    };
    data.push(record);
  }

  return data;
};

// 필터 규칙 컴포넌트
const FilterRule = ({ rule, index, onUpdate, onDelete }) => {
  const operators = [">", "<", ">=", "<=", "==", "!="];
  const variables = [
    "age",
    "weight",
    "height",
    "bmi",
    "heart_rate",
    "systolic",
    "diastolic",
    "temperature",
    "saturation",
  ];

  const handleChange = (field, value) => {
    onUpdate(index, { ...rule, [field]: value });
  };

  return (
    <div className="filter-rule">
      {index > 0 && (
        <select
          value={rule.connector}
          onChange={(e) => handleChange("connector", e.target.value)}
          className="rule-connector"
        >
          <option value="AND">AND</option>
          <option value="OR">OR</option>
        </select>
      )}

      <select
        value={rule.variable}
        onChange={(e) => handleChange("variable", e.target.value)}
        className="rule-variable"
      >
        <option value="">변수 선택</option>
        {variables.map((v) => (
          <option key={v} value={v}>
            {v.replace("_", " ")}
          </option>
        ))}
      </select>

      <select
        value={rule.operator}
        onChange={(e) => handleChange("operator", e.target.value)}
        className="rule-operator"
      >
        <option value="">조건 선택</option>
        {operators.map((op) => (
          <option key={op} value={op}>
            {op}
          </option>
        ))}
      </select>

      <input
        type="text"
        placeholder="값 입력"
        value={rule.value}
        onChange={(e) => handleChange("value", e.target.value)}
        className="rule-value"
      />

      <button onClick={() => onDelete(index)} className="delete-rule-btn">
        <FiTrash2 />
      </button>
    </div>
  );
};

const DQTPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterRules, setFilterRules] = useState([
    { id: Date.now(), variable: "", operator: "", value: "", connector: "AND" },
  ]);
  const [filterEnabled, setFilterEnabled] = useState(false);
  const [showRuleBuilder, setShowRuleBuilder] = useState(false);

  // 데이터 로드
  useEffect(() => {
    setLoading(true);
    // 실제 애플리케이션에서는 API 호출을 사용할 것입니다
    const dummyData = generateDummyData();
    setData(dummyData);
    setLoading(false);
  }, []);

  // 새 규칙 추가
  const addRule = () => {
    setFilterRules([
      ...filterRules,
      {
        id: Date.now(),
        variable: "",
        operator: "",
        value: "",
        connector: "AND",
      },
    ]);
  };

  // 규칙 업데이트
  const updateRule = (index, updatedRule) => {
    const newRules = [...filterRules];
    newRules[index] = updatedRule;
    setFilterRules(newRules);
  };

  // 규칙 삭제
  const deleteRule = (index) => {
    const newRules = filterRules.filter((_, i) => i !== index);
    setFilterRules(newRules);
  };

  // 필터 적용
  const applyFilters = () => {
    setFilterEnabled(true);
  };

  // 필터 초기화
  const resetFilters = () => {
    setFilterEnabled(false);
  };

  // 필터링된 데이터
  const filteredData = useMemo(() => {
    if (!filterEnabled || filterRules.length === 0) return data;

    return data.filter((item) => {
      let result = true;

      for (let i = 0; i < filterRules.length; i++) {
        const rule = filterRules[i];

        // 규칙이 완전하지 않은 경우 건너뜀
        if (!rule.variable || !rule.operator || rule.value === "") continue;

        let itemValue = Number(item[rule.variable]);
        let ruleValue = Number(rule.value);
        let conditionResult;

        switch (rule.operator) {
          case ">":
            conditionResult = itemValue > ruleValue;
            break;
          case "<":
            conditionResult = itemValue < ruleValue;
            break;
          case ">=":
            conditionResult = itemValue >= ruleValue;
            break;
          case "<=":
            conditionResult = itemValue <= ruleValue;
            break;
          case "==":
            conditionResult = itemValue === ruleValue;
            break;
          case "!=":
            conditionResult = itemValue !== ruleValue;
            break;
          default:
            conditionResult = true;
        }

        if (i === 0) {
          result = conditionResult;
        } else {
          if (rule.connector === "AND") {
            result = result && conditionResult;
          } else if (rule.connector === "OR") {
            result = result || conditionResult;
          }
        }
      }

      return result;
    });
  }, [data, filterRules, filterEnabled]);

  if (loading) {
    return <div className="dqt-container loading">데이터 로딩 중...</div>;
  }

  return (
    <div className="dqt-container">
      <div className="dqt-header">
        <h2>데이터 품질 관리</h2>
        <div className="dqt-actions">
          <button
            className="toggle-filter-btn"
            onClick={() => setShowRuleBuilder(!showRuleBuilder)}
          >
            <FiFilter /> 필터 규칙 {showRuleBuilder ? "숨기기" : "설정"}
          </button>
        </div>
      </div>

      {showRuleBuilder && (
        <div className="filter-builder">
          <h3>필터 규칙 설정</h3>
          <div className="filter-rules">
            {filterRules.map((rule, index) => (
              <FilterRule
                key={rule.id}
                rule={rule}
                index={index}
                onUpdate={updateRule}
                onDelete={deleteRule}
              />
            ))}
          </div>

          <div className="filter-actions">
            <button className="add-rule-btn" onClick={addRule}>
              <FiPlus /> 규칙 추가
            </button>
            <button className="apply-filter-btn" onClick={applyFilters}>
              <FiCheck /> 필터 적용
            </button>
            <button className="reset-filter-btn" onClick={resetFilters}>
              필터 초기화
            </button>
          </div>
        </div>
      )}

      <div className="dqt-summary">
        <div className="summary-card">
          <h3>전체 데이터</h3>
          <p className="summary-value">{data.length}</p>
        </div>
        <div className="summary-card">
          <h3>필터링된 데이터</h3>
          <p className="summary-value">{filteredData.length}</p>
        </div>
        <div className="summary-card">
          <h3>정상 데이터</h3>
          <p className="summary-value">
            {filteredData.filter((item) => item.status === "정상").length}
          </p>
        </div>
        <div className="summary-card">
          <h3>경고 데이터</h3>
          <p className="summary-value status-warning">
            {filteredData.filter((item) => item.status === "경고").length}
          </p>
        </div>
        <div className="summary-card">
          <h3>오류 데이터</h3>
          <p className="summary-value status-error">
            {filteredData.filter((item) => item.status === "오류").length}
          </p>
        </div>
      </div>

      <div className="dqt-table-container">
        <table className="dqt-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Subject ID</th>
              <th>변수명</th>
              <th>값</th>
              <th>상태</th>
              <th>생성일</th>
              <th>수정일</th>
              <th>작업</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((item) => (
              <tr
                key={item.id}
                className={`status-${
                  item.status === "정상"
                    ? "normal"
                    : item.status === "경고"
                    ? "warning"
                    : "error"
                }`}
              >
                <td>{item.id}</td>
                <td>{item.subjectId}</td>
                <td>{item.variableName.replace("_", " ")}</td>
                <td>{item.value}</td>
                <td>
                  <span
                    className={`status-badge ${
                      item.status === "정상"
                        ? "normal"
                        : item.status === "경고"
                        ? "warning"
                        : "error"
                    }`}
                  >
                    {item.status === "경고" && <FiAlertTriangle />}
                    {item.status === "오류" && <FiAlertTriangle />}
                    {item.status}
                  </span>
                </td>
                <td>{item.createdDate}</td>
                <td>{item.updatedDate}</td>
                <td>
                  <div className="action-buttons">
                    <button className="edit-btn">
                      <FiEdit2 />
                    </button>
                    <button className="delete-btn">
                      <FiTrash2 />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DQTPage;
